/*
 * Alex & Lace — cart feedback layer (ROADMAP Phase 7).
 *
 * The "no cart drawer on add" rule (CLAUDE.md) is only survivable if the add is legible.
 * This module is that legibility layer. It EXTENDS Horizon's cart events — it never
 * replaces Horizon's cart JS. It listens for the bubbling `cart:update` event that
 * Horizon's <product-form-component> dispatches after a successful add (CartAddEvent),
 * and drives the four feedback surfaces that share one source of truth (the free-shipping
 * threshold):
 *
 *   1. PDP "Add to bag" button → "Added ✓" text revert (2s)            [here]
 *   2. Quick-add overlay pill button → "Added ✓" + toast               [al-product-card.js seam calls these helpers]
 *   3. Cart-icon badge pulse                                           [Horizon cart-icon.js + CSS; not driven here]
 *   4. Cart page progress bar + reassurance                            [al-free-shipping-bar.liquid, server-rendered]
 *
 * Plus the at-add transient nudge that briefly content-swaps the PDP reassurance line.
 *
 * Durations (ROADMAP Phase 7): "Added ✓" 2s · transient nudges 3s · (badge ~400ms is CSS).
 * prefers-reduced-motion: text/number swaps still happen, motion does not.
 */

import { formatMoney } from '@theme/money-formatting';

const ADDED_MS = 2000;
const NUDGE_MS = 3000;
const TOAST_MS = 3000;
const TOAST_EXIT_MS = 360;

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const config = () => window.AlCartFeedbackConfig || {};

/** Format minor-unit (cents) amount through the store money format. */
function money(cents) {
  const { moneyFormat, currency } = config();
  const value = Math.max(0, Math.round(cents));
  try {
    // Strip any HTML the store's money_format may wrap around the amount (toasts use textContent).
    return formatMoney(value, moneyFormat, currency).replace(/<[^>]*>/g, '');
  } catch {
    return '$' + (value / 100).toFixed(0);
  }
}

/**
 * The shared free-shipping copy used by BOTH the PDP transient nudge and the overlay toast.
 * One threshold (settings.al_free_shipping_threshold, injected as config.threshold in cents)
 * drives every surface.
 * @param {number|null|undefined} totalCents - cart total after the add
 * @returns {string|null}
 */
function freeShippingNudge(totalCents) {
  const threshold = config().threshold;
  if (!threshold || totalCents == null) return null;
  const remaining = threshold - totalCents;
  return remaining > 0 ? `Add ${money(remaining)} more for free shipping` : 'You unlocked free shipping ✓';
}

// ── "Added ✓" button revert ───────────────────────────────────────────────────
const addedTimers = new WeakMap();
const addedOriginal = new WeakMap();

/**
 * Swap a buy button's label to "Added ✓" for 2s, then revert. Works for the PDP button and
 * the quick-add overlay button (both render snippets/add-to-cart-button.liquid). Horizon's
 * burst icon is suppressed for these buttons in al-cart-feedback.css, so the text is the signal.
 * @param {HTMLElement|null|undefined} button
 */
function markAdded(button) {
  const label = button?.querySelector('.add-to-cart-text__content');
  if (!button || !label) return;

  if (addedTimers.has(button)) {
    clearTimeout(addedTimers.get(button));
  } else {
    addedOriginal.set(button, label.innerHTML);
  }

  label.textContent = 'Added ✓';
  button.classList.add('al-added');

  const timer = setTimeout(() => {
    const original = addedOriginal.get(button);
    if (original != null) label.innerHTML = original;
    button.classList.remove('al-added');
    addedTimers.delete(button);
    addedOriginal.delete(button);
  }, ADDED_MS);

  addedTimers.set(button, timer);
}

// ── PDP at-add transient nudge (reassurance line content-swap) ─────────────────
const NUDGE_FADE_MS = 150;
let nudgeTimer = null;
// The static reassurance markup, captured ONCE. It is invariant (the static "Free shipping
// over $75 · 30-day returns" line), so caching it is safe — and capturing once means we
// never accidentally cache a transient nudge state as if it were the original.
let nudgeStaticHTML = null;

/**
 * Crossfade a content swap: fade out, mutate at the trough, fade back in (Phase 7 #8).
 * Under reduced motion the swap is instant. Blur (in CSS) masks the crossfade.
 * @param {HTMLElement} el
 * @param {() => void} mutate
 */
function crossfade(el, mutate) {
  if (reduceMotion.matches) {
    mutate();
    return;
  }
  el.classList.add('al-fading');
  setTimeout(() => {
    mutate();
    // Apply the new content while still faded, then release to transition back in.
    requestAnimationFrame(() => el.classList.remove('al-fading'));
  }, NUDGE_FADE_MS);
}

/**
 * Briefly swap the reassurance line to the free-shipping nudge, then revert. The nudge text
 * is derived LIVE from the cart total every time (never a stored value); the revert always
 * restores the captured static line. Fixes the earlier bug where the static markup was
 * nulled before the deferred crossfade restore ran, leaving the nudge stuck.
 * @param {number|null|undefined} totalCents - cart total after the add
 */
function nudgeReassurance(totalCents) {
  const line = document.querySelector('.al-reassurance .al-reassurance__line');
  const text = freeShippingNudge(totalCents);
  if (!line || !text) return;

  if (nudgeStaticHTML == null) nudgeStaticHTML = line.innerHTML;
  if (nudgeTimer) clearTimeout(nudgeTimer);

  crossfade(line, () => {
    line.textContent = text;
    line.closest('.al-reassurance')?.classList.add('al-reassurance--nudge');
  });

  nudgeTimer = setTimeout(() => {
    nudgeTimer = null;
    crossfade(line, () => {
      line.innerHTML = nudgeStaticHTML;
      line.closest('.al-reassurance')?.classList.remove('al-reassurance--nudge');
    });
  }, NUDGE_MS);
}

// ── Toast (quick-add overlay surface) ──────────────────────────────────────────
let toastRegion = null;

function ensureToastRegion() {
  if (toastRegion?.isConnected) return toastRegion;
  toastRegion = document.createElement('div');
  toastRegion.className = 'al-toast-region';
  toastRegion.setAttribute('role', 'status');
  toastRegion.setAttribute('aria-live', 'polite');
  document.body.appendChild(toastRegion);
  return toastRegion;
}

/** Show a top-right toast (spatially consistent enter/exit so it reads as one object). */
function showToast(message) {
  if (!message) return;
  const region = ensureToastRegion();
  const toast = document.createElement('div');
  toast.className = 'al-toast';
  toast.textContent = message;
  region.appendChild(toast);

  // Enter on the next frame so the @starting-style / transition fires.
  requestAnimationFrame(() => toast.setAttribute('data-show', ''));

  const dismiss = () => {
    if (!toast.isConnected) return;
    toast.removeAttribute('data-show');
    if (reduceMotion.matches) {
      toast.remove();
    } else {
      let removed = false;
      const done = () => {
        if (removed) return;
        removed = true;
        toast.remove();
      };
      toast.addEventListener('transitionend', done, { once: true });
      setTimeout(done, TOAST_EXIT_MS + 60);
    }
  };
  setTimeout(dismiss, TOAST_MS);
}

/** Overlay-side helper: toast the shared free-shipping copy. */
function toastFreeShipping(totalCents) {
  showToast(freeShippingNudge(totalCents));
}

// Expose the helpers for the quick-add overlay seam (al-product-card.js). No import needed
// there — the seam only runs on a user add, by which point this module has loaded.
window.AlCartFeedback = { markAdded, showToast, toastFreeShipping, freeShippingNudge };

// ── Document listener: PDP surfaces ────────────────────────────────────────────
document.addEventListener('cart:update', (event) => {
  const detail = event.detail || {};
  if (detail.data?.didError) return;
  // Only react to adds (CartAddEvent), not cart-page quantity edits.
  if (detail.data?.source !== 'product-form-component') return;

  const origin = event.target;
  // Quick-add overlay adds are handled at the al-product-card seam (button + toast); skip here
  // so we don't double-fire. The cart-icon badge pulse is Horizon's, and fires for both.
  if (origin instanceof Element && origin.closest('al-product-card')) return;

  const button = origin instanceof Element ? origin.querySelector('.add-to-cart-text__content')?.closest('button') : null;
  markAdded(button);

  nudgeReassurance(detail.resource?.total_price);
});
